// Benepack OMS · identity api wrapper
// SSOT: api/openapi/oms-gateway.yaml /api/v1/identity/auth/*
//
// 注: 本层手写 wrapper · 后续 codegen 跑 `pnpm codegen:openapi` 生成 src/api/generated/
// 时本层可改为 re-export · 业务层不感知。
//
// hotfix #2 (v0.1-frontend K8s 真验暴露 2026-05-16):
//   backend LoginResponse 不返 user 对象 · 只返 user_id (PascalCase)
//   backend /me 返 PascalCase {UserID, Username, DisplayName, Email, OrgID, Active}
//   也不返 permissions / roles 数组 (RBAC sidebar 过滤必需)
//   修法: login 后 me() + JWT decode 合并 · adapter PascalCase → snake_case
//
// 前端补面② (v0.1-frontend-rc24 · 系统用户管理 · admin-only):
//   扩 users CRUD (list/get/create/update/disable) + user-role 分配 (list/grant/revoke)。
//   gateway identity_handler.go:49-59 raw passthrough · response 走 ProtoToUserInfo (PascalCase) ·
//   request 走 protojson (DiscardUnknown:false → 严格只发 proto 字段 · enum 发 proto 名 via enum-codec)。
import { http } from './client';
import { mapEnum, mapEnumOpt, UserStatusToProto } from './enum-codec';
import type {
  UserListItem,
  UserDetailView,
  UserRoleItem,
  RoleListItem,
  RoleDetailView,
  ListWrap,
} from '@/types/views';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface AuthUser {
  id: string;
  username: string;
  display_name: string;
  email?: string | null;
  roles: string[];
  permissions: string[];
}

// hotfix #2: backend 真返结构 (不含 user 对象)
// 2FA WP: 密码校验通过但需二次验证时, 不返真 token, 改返信号 + pre_2fa_token (snake_case ·
//   gateway WriteJSON 序列化 proto json tag)。两者皆 false → 完整登录 (现状字节等价)。
export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  refresh_token_expiry?: { seconds: number; nanos: number };
  user_id: number;
  two_factor_required?: boolean;
  must_enroll_2fa?: boolean;
  pre_2fa_token?: string;
}

// =========== 强制 TOTP 二次验证 (2FA WP) ===========

/** BeginTwoFactorEnrollmentResponse · otpauth_uri (渲二维码) + 明文密钥 (手动录入)。★ 仅本次返回。 */
export interface BeginTwoFactorEnrollmentResponse {
  otpauth_uri: string;
  secret: string;
}

/** ConfirmTwoFactorEnrollmentResponse · 10 个一次性恢复码明文。★ 仅本次返回·务必妥存。 */
export interface RecoveryCodesResponse {
  recovery_codes: string[];
}

/** GetTwoFactorStatusResponse · 只读布尔/计数 (无密钥/码)。 */
export interface TwoFactorStatusResponse {
  enrolled: boolean;
  recovery_codes_remaining: number;
  enforced: boolean;
}

/** VerifyTwoFactorResponse · 成功后与 LoginResponse 同形 (真 token)。 */
export interface VerifyTwoFactorResponse {
  access_token: string;
  refresh_token: string;
  refresh_token_expiry?: { seconds: number; nanos: number };
  user_id: number;
}

/**
 * bearerConfig 构造一个带显式 Bearer 头的请求配置 (_skipAuth=true 防 axios 拦截器
 * 再叠加 localStorage 里的 stale token)。用于强制 enroll 流程——用户此刻只有 pre-2FA token,
 * 还没真 JWT 落 localStorage。token 为空时退化为 _skipAuth (公开调用)。
 */
function bearerConfig(token?: string) {
  return {
    _skipAuth: true,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  } as { _skipAuth: boolean; headers?: Record<string, string> };
}

/**
 * BeginTwoFactorEnrollment · POST /identity/auth/begin-2fa-enrollment (public route)。
 * 鉴权: normal JWT 或 pre-2FA token (强制 enroll 时用户只有 pre-2FA token)。
 *   bearer 显式传入 → 走 Authorization header; 不传 → 用 localStorage 里的真 JWT (自助安全页场景)。
 * ★ 响应含 otpauth_uri + 明文密钥, 仅本次返回, 不入任何持久层/日志。
 */
export async function beginTwoFactorEnrollment(
  bearer?: string,
): Promise<BeginTwoFactorEnrollmentResponse> {
  const cfg = bearer ? bearerConfig(bearer) : undefined;
  const resp = await http.post<BeginTwoFactorEnrollmentResponse>(
    '/api/v1/identity/auth/begin-2fa-enrollment',
    {},
    cfg,
  );
  return resp.data;
}

/**
 * ConfirmTwoFactorEnrollment · POST /identity/auth/confirm-2fa-enrollment (public route)。
 * 提交一枚 TOTP 码确认 pending → 返回 10 恢复码明文。鉴权同 begin。
 */
export async function confirmTwoFactorEnrollment(
  code: string,
  bearer?: string,
): Promise<RecoveryCodesResponse> {
  const cfg = bearer ? bearerConfig(bearer) : undefined;
  const resp = await http.post<RecoveryCodesResponse>(
    '/api/v1/identity/auth/confirm-2fa-enrollment',
    { code },
    cfg,
  );
  return resp.data;
}

/**
 * VerifyTwoFactor · POST /identity/auth/verify-2fa (public route · 两步登录第二步)。
 * pre_2fa_token 走 body (下游 usecase ParsePre2FA 校验)。code = 6 位 TOTP 或一枚恢复码。
 * ★ 成功返回真 access/refresh token。失败 2FA 验证复用共享锁定计数 (5 次/10 分钟)。
 */
export async function verifyTwoFactor(
  preToken: string,
  code: string,
): Promise<VerifyTwoFactorResponse> {
  const resp = await http.post<VerifyTwoFactorResponse>(
    '/api/v1/identity/auth/verify-2fa',
    { pre_2fa_token: preToken, code },
    { _skipAuth: true },
  );
  return resp.data;
}

/** GetTwoFactorStatus · GET /identity/auth/2fa-status (JWT · 自助安全页)。 */
export async function getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  const resp = await http.get<TwoFactorStatusResponse>('/api/v1/identity/auth/2fa-status');
  return resp.data;
}

/**
 * DisableTwoFactor · POST /identity/auth/disable-2fa (JWT · 自助关闭)。
 * body 带一枚 fresh TOTP 码。enforced=true 时后端拒绝 (403 2FA_DISABLE_FORBIDDEN)。
 * ★ 成功后后端已吊销全 session → 调用方须立即 logout 强制重登。
 */
export async function disableTwoFactor(code: string): Promise<void> {
  await http.post('/api/v1/identity/auth/disable-2fa', { code });
}

/**
 * RegenerateRecoveryCodes · POST /identity/auth/regenerate-recovery-codes (JWT · 自助)。
 * body 带一枚 fresh TOTP 码·须已 confirmed → 返回新 10 恢复码明文 (仅本次)。
 */
export async function regenerateRecoveryCodes(code: string): Promise<RecoveryCodesResponse> {
  const resp = await http.post<RecoveryCodesResponse>(
    '/api/v1/identity/auth/regenerate-recovery-codes',
    { code },
  );
  return resp.data;
}

/**
 * 管理员重置 2FA · POST /identity/users/{id}/reset-2fa (user:reset_2fa · admin)。
 * 删目标用户 2FA 密钥 + 全部恢复码 · 吊销其全部 session (强制重登) · 200 空体 (同 unlock)。
 */
export async function resetUserTwoFactor(id: string | number): Promise<void> {
  await http.post(`/api/v1/identity/users/${id}/reset-2fa`);
}

// hotfix #2: backend /me 真返结构 (PascalCase · 不含 permissions/roles)
export interface MeResponse {
  UserID: number;
  TenantID: number;
  Username: string;
  DisplayName: string;
  Email: string;
  OrgID: number;
  Active: boolean;
}

// hotfix #2: JWT payload 结构 (含 permissions/role_ids · RBAC 唯一可靠来源)
export interface JwtPayload {
  sub: string;
  user_id: number;
  tenant_id: number;
  role_ids: number[];
  permissions: string[];
  iat: number;
  exp: number;
  iss: string;
}

// hotfix #2: 标准 JWT base64url decode payload (无第三方依赖)
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // pad 到 4 倍数
    const pad = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4));
    const payloadJson = atob(pad);
    return JSON.parse(payloadJson) as JwtPayload;
  } catch {
    return null;
  }
}

// hotfix #2: 合并 me + JWT 构 AuthUser (PascalCase → snake_case adapter)
export function buildAuthUserFromTokenAndProfile(token: string, profile: MeResponse): AuthUser {
  const payload = decodeJwtPayload(token);
  return {
    id: String(profile.UserID),
    username: profile.Username,
    display_name: profile.DisplayName,
    email: profile.Email || null,
    roles: payload?.role_ids?.map(String) ?? [],
    permissions: payload?.permissions ?? [],
  };
}

export async function login(req: LoginRequest): Promise<LoginResponse> {
  const resp = await http.post<LoginResponse>('/api/v1/identity/auth/login', req);
  return resp.data;
}

export async function logout(): Promise<void> {
  await http.post('/api/v1/identity/auth/logout', {});
}

// =========== 密码管理 (GL-12 · 自助改密 + 管理员重置) ===========

/**
 * ChangePasswordRequest · POST /identity/auth/change-password (JWT 必须 · 任何角色可调)。
 * SSOT: api/proto/identity/v1/auth.proto ChangePasswordRequest · 严格 2 字段
 *   (无 actor_id/tenant_id · 下游 identity 从 Bearer token 解 · protojson DiscardUnknown:false)。
 */
export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

/**
 * 自助改密。★ 成功后后端已吊销该用户全部 session (含当前会话) ——
 * 调用方必须立即走 AuthProvider.logout() 强制重新登录 (别留死 token 撞 401)。
 */
export async function changePassword(req: ChangePasswordRequest): Promise<void> {
  await http.post('/api/v1/identity/auth/change-password', req);
}

// =========== 自助找回密码 (forgot-password · public · 无 JWT) ===========

/**
 * 申请重置链接 · POST /identity/auth/request-password-reset (public · 无 JWT)。
 * ★ 防枚举: 无论邮箱是否注册, 后端都返回同一中性成功 (200) —— 调用方据此对用户统一
 *   显示 "如果该邮箱已注册,我们已发送重置链接", 不暴露邮箱存在性。
 *   唯一可能的错误是 per-IP 限流 (429 RATE_LIMITED)。
 * _skipAuth: 显式不带 Bearer (用户忘了密码本就没登录态)。
 * SSOT: api/proto/identity/v1/auth.proto RequestPasswordResetRequest (email · ip/ua 由 gateway 填)。
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await http.post('/api/v1/identity/auth/request-password-reset', { email }, { _skipAuth: true });
}

/**
 * 用 token + 新密码完成重置 · POST /identity/auth/confirm-password-reset (public · 无 JWT)。
 * token = 重置邮件链接里的 raw token。★ 成功后后端已吊销该用户全部 session ——
 * 跳回登录用新密码重新登录即可。
 * 无效/过期/已用 token → 401 (OMS_IDENTITY_AUTH_TOKEN_INVALID/EXPIRED)；新密码弱 → 422。
 * SSOT: api/proto/identity/v1/auth.proto ConfirmPasswordResetRequest (token/new_password · ip/ua 由 gateway 填)。
 */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await http.post(
    '/api/v1/identity/auth/confirm-password-reset',
    { token, new_password: newPassword },
    { _skipAuth: true },
  );
}

/** ResetPasswordRequest 请求体 (user_id 走 path · tenant_id 由 gateway 注入 · 仅发 new_password)。 */
export interface ResetUserPasswordRequest {
  new_password: string;
}

/**
 * 管理员重置密码 · POST /identity/users/{id}/reset-password (user:reset_password · admin)。
 * ★ 成功后目标用户全部 session 已被吊销 (强制下线) · 200 空体 (同 disableUser)。
 */
export async function resetUserPassword(
  id: string | number,
  req: ResetUserPasswordRequest,
): Promise<void> {
  await http.post(`/api/v1/identity/users/${id}/reset-password`, req);
}

/**
 * 管理员解锁账号 · POST /identity/users/{id}/unlock (复用 user:reset_password · admin)。
 * 语义: 清 login_failed_count/locked_until · locked→active · 不碰密码 · 不吊销 session
 *   (与 reset-password 强制下线相反 · 已登录会话继续有效) · 无 body · 200 空体。
 */
export async function unlockUser(id: string | number): Promise<void> {
  await http.post(`/api/v1/identity/users/${id}/unlock`);
}

// hotfix #2: 返 MeResponse (PascalCase 原样) · AuthProvider 走 adapter
export async function me(): Promise<MeResponse> {
  const resp = await http.get<MeResponse>('/api/v1/identity/me');
  return resp.data;
}

// =========== User CRUD (前端补面② · admin-only · gateway raw passthrough) ===========

/** UI 友好短名 · 提交前由 enum-codec → proto 枚举值名 (USER_STATUS_*)。 */
export type UserStatusShort = 'active' | 'locked' | 'disabled';

/**
 * ListUsersRequest · GET /identity/users 查询参数。
 * ★ gateway ListUsers (identity_handler.go:146) 仅透传 page/size + tenant (从 ctx)。
 *   proto ListUsersRequest 虽有 org_id/status 字段 · 但 handler 未解析 query → 下列 filter
 *   为 forward-compat 预留 · 当前服务端忽略 (同 master "List filter 一期未解析" · 见 audit-slice actor 筛选)。
 */
export interface ListUsersRequest {
  page?: number;
  size?: number;
  org_id?: number; // 预留 · 当前 gateway 忽略
  status?: UserStatusShort; // 预留 · 当前 gateway 忽略
}

/** ListUsersResponse · gateway ProtoToUserListView · { items: UserInfo[], page } (PascalCase items)。 */
export type ListUsersResponse = ListWrap<UserListItem>;

/**
 * CreateUserRequest · POST /identity/users 请求体 (前端表单形状)。
 * SSOT: api/proto/identity/v1/identity.pb.go:CreateUserRequest
 *   字段: username/display_name/email/password/status/org_id (tenant_id 由 gateway 从 ctx 注入)。
 *   ★ password 仅 create 有 (proto UpdateUserRequest 无)。enum status 提交前转 proto 名。
 */
export interface CreateUserRequest {
  username: string;
  display_name?: string;
  email?: string;
  password: string;
  status: UserStatusShort;
  org_id?: number; // 0 = NULL
}

/**
 * UpdateUserRequest · PUT /identity/users/{id} 请求体。
 * SSOT: identity.pb.go:UpdateUserRequest · 仅 org_id/display_name/email/status
 *   (★ 无 username/password · proto 不可改 · 严格只发这 4 字段 · 否则 protojson DiscardUnknown:false → 400)。
 */
export interface UpdateUserRequest {
  display_name?: string;
  email?: string;
  status?: UserStatusShort;
  org_id?: number;
}

export async function listUsers(req: ListUsersRequest): Promise<ListUsersResponse> {
  const resp = await http.get<ListUsersResponse>('/api/v1/identity/users', { params: req });
  return resp.data;
}

export async function getUser(id: string | number): Promise<UserDetailView> {
  const resp = await http.get<UserDetailView>(`/api/v1/identity/users/${id}`);
  return resp.data;
}

export async function createUser(req: CreateUserRequest): Promise<UserDetailView> {
  // ★ 严格只发 proto CreateUserRequest 字段 (DiscardUnknown:false) · enum 发 proto 名 (protojson 收名/int)
  const wire = {
    username: req.username,
    display_name: req.display_name ?? '',
    email: req.email ?? '',
    password: req.password,
    status: mapEnum(UserStatusToProto, req.status),
    org_id: req.org_id ?? 0,
  };
  const resp = await http.post<UserDetailView>('/api/v1/identity/users', wire);
  return resp.data;
}

export async function updateUser(
  id: string | number,
  req: UpdateUserRequest,
): Promise<UserDetailView> {
  // ★ 严格只发 UpdateUserRequest 字段 (无 username/password · 否则 protojson DiscardUnknown:false → 400)
  const wire: Record<string, unknown> = {};
  if (req.display_name !== undefined) wire.display_name = req.display_name;
  if (req.email !== undefined) wire.email = req.email;
  if (req.org_id !== undefined) wire.org_id = req.org_id;
  const st = mapEnumOpt(UserStatusToProto, req.status);
  if (st !== undefined) wire.status = st;
  const resp = await http.put<UserDetailView>(`/api/v1/identity/users/${id}`, wire);
  return resp.data;
}

/** DisableUser · DELETE /identity/users/{id} (语义=禁用非物理删除 · 200 空体)。 */
export async function disableUser(id: string | number): Promise<void> {
  await http.delete(`/api/v1/identity/users/${id}`);
}

// =========== User-Role 分配 (前端补面② · user_role:assign · admin-only) ===========

/** ListUserRolesResponse · gateway ProtoToUserRoleListView · { items: RoleInfo[] } (无 paging)。 */
interface ListUserRolesResponse {
  items: UserRoleItem[];
}

export async function listUserRoles(userId: string | number): Promise<UserRoleItem[]> {
  const resp = await http.get<ListUserRolesResponse>(`/api/v1/identity/users/${userId}/roles`);
  return resp.data.items ?? [];
}

/** GrantUserRole · POST /identity/users/{id}/roles · body { role_id } (raw role_id · MVP · ListRoles 未暴露)。 */
export async function grantUserRole(userId: string | number, roleId: number): Promise<void> {
  await http.post(`/api/v1/identity/users/${userId}/roles`, { role_id: roleId });
}

/** RevokeUserRole · DELETE /identity/users/{id}/roles/{roleId}。 */
export async function revokeUserRole(userId: string | number, roleId: number): Promise<void> {
  await http.delete(`/api/v1/identity/users/${userId}/roles/${roleId}`);
}

// =========== Role 只读 (前端补面⑤ · role:read · admin/manager) ===========

/**
 * ListRolesRequest · GET /identity/roles 查询参数。
 * ★ gateway ListRoles (identity_handler.go) 仅透传 page/size + tenant (从 ctx)；同 ListUsers。
 *   proto ListRolesRequest 无 status 字段 (角色列表不按状态筛选)。
 */
export interface ListRolesRequest {
  page?: number;
  size?: number;
}

/** ListRolesResponse · gateway ProtoToRoleListView · { items: RoleInfo[], page } (PascalCase items)。 */
export type ListRolesResponse = ListWrap<RoleListItem>;

export async function listRoles(req: ListRolesRequest = {}): Promise<ListRolesResponse> {
  const resp = await http.get<ListRolesResponse>('/api/v1/identity/roles', { params: req });
  return resp.data;
}

export async function getRole(id: string | number): Promise<RoleDetailView> {
  const resp = await http.get<RoleDetailView>(`/api/v1/identity/roles/${id}`);
  return resp.data;
}
